<template lang="pug">
	.ai-chat(v-if="showed" :class="{collapsed: collapsed, resizing: resizing}" :style="{left: `${left}px`, right: `${right}px`, height: `${collapsed ? collapsedHeight : height}px`}" v-on:dragenter="onAttachmentsDragEnter($event)" v-on:dragover="onAttachmentsDragOver($event)" v-on:dragleave="onAttachmentsDragLeave($event)" v-on:drop="onAttachmentsDrop($event)")
		//- Прозрачный оверлей на весь экран во время ресайза, чтобы курсор над iframe не прерывал drag
		.ai-chat-resize-overlay(v-if="resizing")
		AIAgentDropOverlay(v-if="attachmentsDragging")
		//- Верхний край: за него тянем чтобы изменить высоту. В свёрнутом состоянии остаётся только иконка AI
		.ai-chat-resize-handle(v-on:mousedown="startResize($event)")
			.ai-chat-collapsed-icon(v-if="collapsed" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: 'Drag up or click to open AI chat'}")
				i.fa.fa-magic
				span AI Chat
				i.fa.fa-exclamation-circle.ai-chat-collapsed-error(v-if="isPreviewErrorShown()" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: 'The preview failed with an error'}")
		.ai-chat-body(v-if="!collapsed")
			AIAgentHistory(ref="history" :chat="chat" :messages="messages" :thinking="thinking" :stopping="stopping" :transcribing="transcribing" :preview-error="isPreviewErrorShown() ? previewError : null" :confirming-message-id="confirmingMessageId" v-on:answer-confirmation="answerConfirmation" v-on:fix-preview-error="askAgentToFixPreviewError()" v-on:dismiss-preview-error="dismissPreviewError()" v-on:stop="stopAgent()")
			.ai-chat-input
				AIAgentChatTitle(:chat="chat" :budget-description="availableBudgetDescription")
				AIAgentPendingAttachments(v-if="pendingAttachments.length || pendingSkippedAttachments.length || attachmentsPreparing || attachmentsUploading" :entries="pendingAttachments" :skipped="pendingSkippedAttachments" :preparing="attachmentsPreparing" :uploading="attachmentsUploading" v-on:remove="removePendingAttachment($event)" v-on:clear="clearPendingAttachments()")
				textarea(ref="textarea" v-model="prompt" rows="3" placeholder="Type your message..." :style="{maxHeight: `${getTextareaMaxHeight()}px`}" v-on:input="autoResizeTextarea()" v-on:keydown.enter.exact.prevent="sendMessage()")
				AIAgentControls(:chats="chats" :chat="chat" :messages="messages" :prompt="prompt" :model-codename="modelCodename" :model-items="modelItems" :text-models-by-codename="textModelsByCodename" :send-available="isSendAvailable()" :attachments-busy="!!(attachmentsPreparing || attachmentsUploading)" :preview-error-catching="previewErrorCatching" v-on:select-chat="selectChat($event)" v-on:set-model="setModel($event)" v-on:attach-files="addAttachments($event)" v-on:toggle-preview-errors="togglePreviewErrorCatching()" v-on:send="sendMessage()")
</template>

<script>
	import Screen 						from './../../mixins/screen'

	import { Layout } 					from './../../services/Layout'
	import { Metadata } 				from './../../services/Metadata'
	import { Version } 					from './../../services/Version'
	import { Creative } 					from './../../services/Creative'
	import { User } 					from './../../services/User'
	import { AIAgent } 					from './../../services/AIAgent'
	import { Server } 					from './../../services/Server'
	import { Notifications } 			from './../../services/Notifications'

	import AIAgentResize				from './AIAgent.Resize'
	import AIAgentPreviewErrors			from './AIAgent.PreviewErrors'
	import AIAgentAttachments			from './AIAgent.Attachments'

	import AIAgentDropOverlay			from './AIAgent.DropOverlay'
	import AIAgentHistory				from './AIAgent.History'
	import AIAgentChatTitle				from './AIAgent.ChatTitle'
	import AIAgentPendingAttachments	from './AIAgent.PendingAttachments'
	import AIAgentControls				from './AIAgent.Controls'

	// Компонент ИИ чата под превью. Части компонента:
	// - примеси: AIAgent.ResizeMixin (высота и сворачивание), AIAgent.PreviewErrorsMixin (ошибки фрейма превью), AIAgent.AttachmentsMixin (файлы для отправки);
	// - дочерние компоненты: AIAgent.DropOverlay, AIAgent.History (сообщения AIAgent.Message*.vue, AIAgent.PreviewError, AIAgent.Indicators),
	//   AIAgent.ChatTitle, AIAgent.PendingAttachments, AIAgent.Controls (AIAgent.VoiceControls)
	export default {
		name: "AIAgent",
		mixins: [Screen, AIAgentResize, AIAgentPreviewErrors, AIAgentAttachments],
		screen: {
			updateOn: [
				"Metadata Loaded",
				"Aside State Changed",
				"Creative AI Budget Changed",
				"Publisher AI Budget Changed",
				"User AI Budget Changed",
				"Selected Creative Updated"
			],
			events: {
				// Broadcast хранит только один обработчик события на компонент (повторная подписка заменяет предыдущую),
				// поэтому каждое событие указывается только в одном ключе
				"Selected Version Switched,Selected Creative Fully Loaded": function() {

					this.blockPreviewErrors();

					this.dismissPreviewError();

					this.restoreHeight();

					this.restorePreviewErrorCatching();

					this.refreshChats();

					this.checkVisibility();

				},
				// Чат показывается только на нужных разделах и только при выбранной версии (её может не быть: нет креативов нужного типа или версий в креативе)
				"Layout Page Changed,Selected Creative Changed,Selected Version Removed": function() {

					this.checkVisibility();

				},
				"Preview Size Changed,Preview Device Changed,Preview Orientation Changed": function() {

					this.restoreHeight();

				},
				"Creative AI Chats Loaded,AI Chats Changed": function() {

					this.refreshChats();

				},
				// Сервер сообщил id чата созданного этим клиентом - сразу переключаемся на него
				"AI Chat Created": function(chatId, versionId) {

					if (versionId !== Version.SelectedVersion?.id) return;

					this.refreshChats();

					if (AIAgent.Chats[chatId]) this.selectChat(AIAgent.Chats[chatId]);

				},
				"AI Chat Messages Loaded": function(chatId) {

					if (chatId === this.chat?.id) this.refreshMessages();

				},
				"AI Chat Message Added": function(message) {

					if (message["chat_id"] !== this.chat?.id) return;

					// Сообщение агента пришло - ожидание ответа на отправленное сообщение дальше определяется состоянием агента с сервера
					if (message.role === 'assistant') this.sending = false;

					// Распознанный текст голосового сообщения пришёл - ожидание распознавания дальше определяется состоянием с сервера
					if (message.options?.voice || message.options?.type === 'voice') this.voiceSending = false;

					this.refreshMessages();

				},
				// Изменилось уже показанное сообщение (например, статус запроса подтверждения удаления)
				"AI Chat Message Changed": function(message) {

					if (message.id === this.confirmingMessageId) this.confirmingMessageId = null;

					if (message["chat_id"] === this.chat?.id) this.refreshMessages();

				},
				// Агент начал или закончил работу (без параметров - состояния сброшены при разрыве соединения)
				"AI Chat Agent State Changed": function(chatId) {

					if (chatId === undefined || chatId === this.chat?.id) this.sending = false;

					this.refreshThinking();

				},
				// Сервер сообщил, что аудио отправлено на распознавание либо распознано (без параметров - состояния сброшены при разрыве соединения)
				"AI Chat Voice State Changed": function(chatId) {

					if (chatId === undefined || chatId === this.chat?.id) this.voiceSending = false;

					this.refreshThinking();

				},
				// Аудио отправлено на сервер по HTTP - сразу показываем индикатор распознавания, не дожидаясь ответа сервера
				"AI Chat Voice Message Sent": function(versionId, chatId) {

					if (versionId !== Version.SelectedVersion?.id || (chatId || null) !== (this.chat?.id || null)) return;

					this.voiceSending = true;

					this.refreshThinking();

				},
				"AI Chat Voice Message Send Failed": function() {

					this.voiceSending = false;

					this.refreshThinking();

					Notifications.showError('Voice message upload failed');

				},
				// Ошибка выполнения кода во фрейме превью (перехватывается скриптом Settings.PREVIEW_ERROR_CATCHER_SCRIPT)
				"IFrame Data:PreviewFrameError": function(error) {

					this.setPreviewError(error);

				},
				// Фрейм превью перезагружается - старая ошибка неактуальна (если она повторится, скрипт пришлёт её снова)
				"Refresh Iframe": function() {

					this.dismissPreviewError();

				}
			}
		},
		components: {
			AIAgentDropOverlay,
			AIAgentHistory,
			AIAgentChatTitle,
			AIAgentPendingAttachments,
			AIAgentControls
		},
		data() {

			this.textModelsByCodename = {};
			this.modelItems = [];

			return {
				showed: false,
				left: 0,
				right: 0,
				// Чаты выбранной версии и текущий выбранный чат (null если чатов в версии ещё нет)
				chats: [],
				chat: null,
				messages: [],
				// Агент работает в текущем чате (показываем "Thinking...")
				thinking: false,
				// Пользователь нажал Stop - ждём, пока сервер прервёт работу агента
				stopping: false,
				// Сообщение отправлено, но сервер ещё не сообщил о начале работы агента - показываем индикатор сразу, не дожидаясь ответа
				sending: false,
				// Голосовое сообщение распознаётся (показываем "Transcribing voice message...")
				transcribing: false,
				// Аудио отправлено на сервер, но сервер ещё не сообщил о начале распознавания
				voiceSending: false,
				// id сообщения с запросом подтверждения, ответ на который отправлен на сервер (кнопки блокируются до ответа сервера)
				confirmingMessageId: null,
				prompt: '',
				modelCodename: null,
				availableBudgetDescription: null
			};

		},
		mounted() {

			this.restoreHeight();

			this.restorePreviewErrorCatching();

			this.refreshChats();

			this.checkVisibility();

			this.onWindowResize = () => this.clampHeight();

			window.addEventListener('resize', this.onWindowResize);

			Layout.setAIAgentHeight(this.height, this.collapsed);

		},
		beforeDestroy() {

			window.removeEventListener('resize', this.onWindowResize);

			this.stopResize();

			this.clearPendingAttachments();

			Layout.setAIAgentHeight(Layout.aiChatDefaultHeight, true);

		},
		methods: {
			collectData() {

				// Позиция: слева начинаем после списка креативов и списка версий, справа заканчиваем перед aside (если он открыт)
				this.left = Layout.getCreativesPlaneWidth() + Layout.creativesVersionsPlaneWidth;
				this.right = Layout.asideOpened ? Layout.asideSize : 0;

				this.availableBudgetDescription = this.getAvailableAIBudgetDescription();

				// Для выбора в чате используются только текстовые модели из метаданных, отсортированные по приоритету
				let textModels = _.sortBy(_.filter(Metadata.AIModels, modelProps => modelProps.type === 'text'), 'priority');

				this.textModelsByCodename = _.keyBy(textModels, 'codename');

				this.modelItems = _.map(textModels, modelProps => {
					let expensivity = modelProps.expensivity ? ` (${modelProps.expensivity})` : '';
					let multimodal = modelProps.multimodal ? `, ${_.replace(modelProps.multimodal, /,/g, ', ')}` : '';
					return {
						..._.pick(modelProps, ['title']),
						description: `by ${modelProps.developer}${expensivity}${multimodal}`,
						disabled: !modelProps.active,
						data: {itemValue: modelProps.codename}
					};
				});

				// Если модель ещё не выбрана или выбранной модели больше нет в метаданных - выбираем сохранённую в localStorage или модель по умолчанию (options.default === true)
				if (!this.textModelsByCodename[this.modelCodename]) {

					let storedModel = window.localStorage.getItem('ai-chat-selected-model');

					if (storedModel && this.textModelsByCodename[storedModel]?.active) {

						this.modelCodename = storedModel;

					} else {

						this.modelCodename = this.getDefaultModelCodename();

					}

				}

			},
			// Описание доступного ИИ бюджета в текущем креативе по источникам в порядке траты (как на экране настроек ИИ креатива)
			getAvailableAIBudgetDescription() {

				if (!Creative.SelectedCreative) return null;

				return Creative.getAvailableAIBudgetDescription(Creative.SelectedCreative, !!(User.currentUser?.Tags?.['admin'] || User.currentUser?.Tags?.['super-admin']), true);

			},
			// Возвращает кодовое имя текстовой модели по умолчанию (options.default === true) или первой активной
			getDefaultModelCodename() {

				let defaultModel = _.find(this.textModelsByCodename, modelProps => modelProps.active && modelProps.options?.default);
				let activeModel = defaultModel || _.find(this.textModelsByCodename, modelProps => modelProps.active);

				return activeModel ? activeModel.codename : null;

			},
			// Обновляем список чатов выбранной версии и определяем какой чат показывать:
			// последний выбранный (из localStorage) если он ещё существует, иначе последний из доступных
			refreshChats() {

				let versionId = Version.SelectedVersion?.id;

				this.chats = versionId ? AIAgent.getVersionChats(versionId) : [];

				let chat = versionId ? _.find(this.chats, {id: AIAgent.getSelectedChatId(versionId)}) : null;

				if (!chat) chat = this.chats.length ? _.last(this.chats) : null;

				this.selectChat(chat);

			},
			selectChat(chat) {

				// Ожидание ответа на сообщение без чата переносим на созданный сервером чат, при обычном переключении чата - сбрасываем
				if (this.chat?.id !== chat?.id && this.chat) {

					this.sending = false;
					this.voiceSending = false;

				}

				this.chat = chat;

				if (chat?.options?.model && this.textModelsByCodename[chat.options.model]) {

					this.modelCodename = chat.options.model;

				} else {

					let storedModel = window.localStorage.getItem('ai-chat-selected-model');

					if (storedModel && this.textModelsByCodename[storedModel]?.active) {

						this.modelCodename = storedModel;

					} else {

						this.modelCodename = this.getDefaultModelCodename();

					}

				}

				if (Version.SelectedVersion) AIAgent.setSelectedChatId(Version.SelectedVersion.id, chat ? chat.id : null);

				// Если сообщения этого чата ещё не запрашивались у сервера - запрашиваем (сервер запомнит и будет присылать новые сообщения автоматически)
				if (chat && !AIAgent.isChatMessagesLoaded(chat.id)) {

					AIAgent.setChatMessagesRequested(chat.id);

					Server.sendToServer({
						LoadAiChatMessages: {
							chatId: chat.id
						}
					});

				}

				this.refreshMessages();

			},
			refreshMessages() {

				// Копия массива - чтобы перерисовать и заменённые сообщения (сообщения сервиса AIAgent не являются реактивными)
				this.messages = this.chat ? AIAgent.getChatMessages(this.chat.id).slice() : [];

				this.refreshThinking();

			},
			// Индикатор "Thinking..." показываем пока сервер сообщает, что агент работает в текущем чате, либо сразу после отправки сообщения
			// Индикатор "Transcribing voice message..." - пока сервер распознаёт голосовое сообщение в текущем чате, либо сразу после отправки аудио
			refreshThinking() {

				this.thinking = this.sending || (this.chat ? AIAgent.isAgentProcessing(this.chat.id) : false);
				this.transcribing = this.voiceSending || (this.chat ? AIAgent.isChatTranscribing(this.chat.id) : false);

				if (!this.thinking) this.stopping = false;

				this.$nextTick(() => this.scrollHistoryToBottom());

			},
			scrollHistoryToBottom() {

				if (this.$refs.history) this.$refs.history.scrollToBottom();

			},
			checkVisibility() {

				// Разделы, в которых показывается чат, заданы в Layout.aiChatPages
				let isVisible = _.includes(Layout.aiChatPages, Layout.page) && Boolean(Version.SelectedVersion);

				if (isVisible) {

					if (!this.showed) this.show();
					else this.update();

				}
				else if (this.showed) this.hide();

				// Скрытый чат не занимает место под превью-фреймом
				Layout.setAIAgentHeight(this.height, this.collapsed || !isVisible);

			},
			// Ответ пользователя на запрос агента на удаление креатива / версии (confirmed = true - Delete, false - Cancel)
			answerConfirmation(message, confirmed, steps = 0) {

				if (this.confirmingMessageId) return;

				this.confirmingMessageId = message.id;

				Server.sendToServer({
					AIAgentConfirmAction: {
						chatId: message["chat_id"],
						messageId: message.id,
						confirmed,
						steps
					}
				});

			},
			// Досрочная остановка агента в текущем чате: сервер прервёт запрос к модели, запишет в чат сообщение об остановке и сообщит о завершении работы агента
			stopAgent() {

				if (!this.chat || this.stopping) return;

				this.stopping = true;

				Server.sendToServer({
					AIAgentStop: {
						chatId: this.chat.id
					}
				});

			},
			setModel(modelCodename) {

				this.modelCodename = modelCodename;

				if (modelCodename) {

					window.localStorage.setItem('ai-chat-selected-model', modelCodename);

					if (this.chat && this.chat.options?.model !== modelCodename) {

						this.chat.options = this.chat.options || {};
						this.chat.options.model = modelCodename;

						Server.sendToServer({
							AiChatSetModel: {
								chatId: this.chat.id,
								model: modelCodename
							}
						});

					}

				}

			},
			// Отправка доступна, если есть текст или добавленные файлы и файлы сейчас не разбираются и не отправляются
			isSendAvailable() {

				if (this.attachmentsPreparing || this.attachmentsUploading) return false;

				return !!(this.prompt.trim() || this.pendingAttachments.length || this.pendingSkippedAttachments.length);

			},
			sendMessage() {

				let content = this.prompt.trim();

				if (!this.isSendAvailable() || !Version.SelectedVersion) return;

				// С добавленными файлами текст сообщения отправляется вместе с ними
				if (this.pendingAttachments.length || this.pendingSkippedAttachments.length) return this.sendAttachments(content);

				this.sendUserMessage(content);

				this.prompt = '';

				this.$nextTick(() => this.autoResizeTextarea());

			},
			sendUserMessage(content) {

				if (!Version.SelectedVersion) return;

				// id чата может быть null - тогда сервер создаст новый чат и сообщит его id событием AI Chat Created
				Server.sendToServer({
					AIAgentUserMessage: {
						chatId: this.chat ? this.chat.id : null,
						versionId: Version.SelectedVersion.id,
						message: content,
						model: this.modelCodename
					}
				});

				// Сразу показываем индикатор ожидания - сервер подтвердит состояние агента событием AIAgentAgentState
				this.sending = true;

				this.refreshThinking();

			}
		}
	}
</script>

<style lang="sass">

	.ai-chat
		position: fixed
		bottom: 0
		z-index: 150
		display: flex
		flex-direction: column
		background-color: #343b41
		border-top: 1px solid #23282c
		color: #f3f4f5
		transition: left 0.2s ease-in-out, right 0.25s ease-in-out
		user-select: none

		&.resizing
			transition: none

		.ai-chat-resize-overlay
			position: fixed
			top: 0
			left: 0
			right: 0
			bottom: 0
			width: 100vw
			height: 100vh
			z-index: 99999
			cursor: ns-resize
			pointer-events: auto !important
			user-select: none

		// Файлы, сгруппированные по типам (в сообщении чата и в списке файлов для отправки)
		.ai-chat-attachments-group
			margin-top: 6px
			white-space: normal

			.ai-chat-attachments-group-title
				display: flex
				align-items: center
				gap: 6px
				margin-bottom: 4px
				font-size: 12px
				font-weight: 600

			.ai-chat-attachments-files
				display: flex
				flex-wrap: wrap
				gap: 6px

			.ai-chat-attachment
				position: relative
				display: flex
				flex-direction: column
				align-items: center
				width: 72px
				padding: 4px
				background-color: rgba(0,0,0,0.2)
				border: 1px solid rgba(255,255,255,0.15)
				border-radius: 4px

				&.unsupported
					border-color: #f86c6b

					.ai-chat-attachment-preview i.fa
						color: #f86c6b

				.ai-chat-attachment-preview
					display: flex
					align-items: center
					justify-content: center
					width: 62px
					height: 48px

					img, video
						max-width: 100%
						max-height: 100%
						object-fit: contain

					i.fa
						font-size: 22px
						opacity: 0.8

				.ai-chat-attachment-name
					width: 100%
					margin-top: 3px
					font-size: 10px
					line-height: 12px
					text-align: center
					white-space: nowrap
					overflow: hidden
					text-overflow: ellipsis

				.ai-chat-attachment-error
					font-size: 10px
					line-height: 12px
					color: #f86c6b

				.ai-chat-attachment-remove
					position: absolute
					top: -6px
					right: -6px
					font-size: 14px
					color: #adb6bf
					background-color: #343b41
					border-radius: 50%
					cursor: pointer
					opacity: 0
					transition: opacity 0.2s ease-in-out, color 0.2s ease-in-out

					&:hover
						color: #f86c6b

				&:hover .ai-chat-attachment-remove
					opacity: 1

		&.collapsed
			background-color: transparent
			border-top: none
			pointer-events: none

			.ai-chat-resize-handle
				height: 100%
				background-color: transparent
				pointer-events: none

				&:hover, &:active
					background-color: transparent

		.ai-chat-resize-handle
			position: relative
			flex: 0 0 auto
			height: 6px
			cursor: ns-resize
			background-color: #23282c
			transition: background-color 0.2s ease-in-out

			&:hover, &:active
				background-color: #20a8d8

			.ai-chat-collapsed-icon
				display: flex
				flex-direction: row
				align-items: center
				gap: 6px
				position: absolute
				left: 50%
				bottom: 0
				transform: translateX(-50%)
				height: 26px
				padding: 0 14px
				background-color: #343b41
				border: 1px solid #23282c
				border-bottom: none
				border-radius: 8px 8px 0 0
				color: #f3f4f5
				font-size: 12px
				font-weight: bold
				cursor: pointer
				pointer-events: auto

				i.fa
					//color: #20a8d8
					margin-top: 1px
					font-size: 11px

				// Признак ошибки превью, которую может исправить ИИ агент
				i.fa.ai-chat-collapsed-error
					color: #f86c6b
					font-size: 13px

				&:hover
					color: #20a8d8

		.ai-chat-body
			display: flex
			flex-direction: column
			flex: 1 1 auto
			min-height: 0

		.ai-chat-input
			flex: 0 0 auto
			display: flex
			flex-direction: column
			padding: 0 15px 10px 15px
			border-top: 1px solid #23282c

			textarea
				width: 100%
				margin-top: 6px
				padding: 6px 10px
				resize: none
				overflow-y: auto
				color: #f3f4f5
				font-size: 13px
				line-height: 18px
				background-color: #515b65
				border: 1px solid #23282c
				border-radius: 0.25rem
				user-select: text

				&:focus
					outline: none
					box-shadow: none
					border-color: rgba(255, 255, 255, 0.3)

				&::placeholder
					color: rgba(255, 255, 255, 0.35)

</style>
